import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DownloadIcon, ReloadIcon } from '@radix-ui/react-icons'
import { PageHeader } from '@/components/layout'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@/components/ui'
import {
  fetchBuildingOfficeHours,
  fetchOfficeHoursAccounts,
  updateBuildingOfficeHours,
} from './api'
import {
  buildOfficeHoursUpdate,
  DEFAULT_OFFICE_HOURS,
  officeHoursValuesSchema,
  getOfficeHoursValues,
} from './officeHoursForm'
import { OfficeHoursTable } from './OfficeHoursTable'

const ACCOUNT_QUERY_KEY = ['office-hours', 'accounts']
const LEGACY_TIMEZONES = [
  'UTC',
  'US/Alaska',
  'US/Arizona',
  'US/Central',
  'US/Eastern',
  'US/Hawaii',
  'US/Mountain',
  'US/Pacific',
]

function getTimeZones() {
  try {
    return [...new Set([...LEGACY_TIMEZONES, ...Intl.supportedValuesOf('timeZone')])].sort()
  } catch {
    return LEGACY_TIMEZONES
  }
}

const TIME_ZONES = getTimeZones()

function BulkSetupForm({ selectedCount, onSubmit, isSaving }) {
  const [values, setValues] = useState(DEFAULT_OFFICE_HOURS)

  const updateField = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    onSubmit(values)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Set selected buildings</CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          Apply one schedule and message to {selectedCount || 'the'} selected building
          {selectedCount === 1 ? '' : 's'}. Saving enables after-hours messages.
        </p>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="bulk-office-start">Office opens</Label>
              <Input
                id="bulk-office-start"
                type="time"
                value={values.startTime}
                onChange={(event) => updateField('startTime', event.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bulk-office-end">After hours start</Label>
              <Input
                id="bulk-office-end"
                type="time"
                value={values.endTime}
                onChange={(event) => updateField('endTime', event.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bulk-office-timezone">Timezone</Label>
              <Input
                id="bulk-office-timezone"
                value={values.timeZone}
                onChange={(event) => updateField('timeZone', event.target.value)}
                list="office-hours-timezones"
                placeholder="America/New_York"
                required
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="bulk-office-message">After-hours message</Label>
            <textarea
              id="bulk-office-message"
              value={values.messageTemplate}
              onChange={(event) => updateField('messageTemplate', event.target.value)}
              rows={4}
              maxLength={10000}
              data-pii="content"
              required
              className="flex min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={!selectedCount || isSaving}>
              {isSaving
                ? 'Saving...'
                : `Set hours for ${selectedCount} building${selectedCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function escapeCsv(value) {
  const text = value == null ? '' : String(value)
  return `"${text.replaceAll('"', '""')}"`
}

function exportRows(rows, drafts, accountName) {
  const columns = [
    ['Portfolio', 'portfolioName'],
    ['Building', 'building'],
    ['Building ID', 'buildingId'],
    ['After-hours enabled', 'afterHoursEnabled'],
    ['Office opens', 'startTime'],
    ['After hours start', 'endTime'],
    ['Timezone', 'timeZone'],
    ['Message', 'messageTemplate'],
  ]
  const lines = [
    columns.map(([label]) => escapeCsv(label)).join(','),
    ...rows.map((row) => {
      const values = getOfficeHoursValues(row, drafts[row.buildingId])
      const exportRow = { ...row, ...values }
      return columns.map(([, key]) => escapeCsv(exportRow[key])).join(',')
    }),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const safeAccountName = (accountName || 'account')
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/-+$/, '')
  link.download = `${safeAccountName}-office-hours.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function OfficeHoursPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [drafts, setDrafts] = useState({})
  const [search, setSearch] = useState('')

  const accountsQuery = useQuery({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: fetchOfficeHoursAccounts,
    staleTime: 2 * 60 * 1000,
  })
  const buildingsQuery = useQuery({
    queryKey: ['office-hours', 'buildings', selectedAccountId],
    queryFn: () => fetchBuildingOfficeHours(selectedAccountId),
    enabled: Boolean(selectedAccountId),
    staleTime: 60 * 1000,
  })
  const updateMutation = useMutation({
    mutationFn: updateBuildingOfficeHours,
    onSuccess: ({ updated }, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['office-hours', 'buildings', String(variables.accountId)],
      })
      if (String(variables.accountId) === selectedAccountId) {
        setDrafts({})
        setSelectedIds([])
      }
      toast.success(`${updated} building${updated === 1 ? '' : 's'} updated.`)
    },
    onError: (error) => {
      toast.error('Office hours were not saved.', {
        description: error.message || 'Try again. If the problem continues, contact engineering.',
      })
    },
  })

  const accounts = accountsQuery.data ?? []
  const rows = buildingsQuery.data ?? []
  const rowsById = useMemo(() => new Map(rows.map((row) => [Number(row.buildingId), row])), [rows])
  const selectedAccount = accounts.find(
    ({ accountId }) => Number(accountId) === Number(selectedAccountId),
  )

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId)
    setSelectedIds([])
    setDrafts({})
    setSearch('')
  }

  const handleToggleBuilding = (buildingId) => {
    setSelectedIds((current) =>
      current.includes(buildingId)
        ? current.filter((id) => id !== buildingId)
        : [...current, buildingId],
    )
  }

  const handleToggleVisible = (visibleIds, allVisibleSelected) => {
    setSelectedIds((current) => {
      if (allVisibleSelected) {
        const visibleIdSet = new Set(visibleIds)
        return current.filter((id) => !visibleIdSet.has(id))
      }
      return [...new Set([...current, ...visibleIds])]
    })
  }

  const handleChangeRow = (buildingId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [buildingId]: { ...current[buildingId], [field]: value },
    }))
  }

  const saveUpdates = async (updates) => {
    if (!selectedAccountId || updates.length === 0) return
    try {
      await updateMutation.mutateAsync({
        accountId: Number(selectedAccountId),
        updates,
      })
    } catch {
      // The mutation's onError callback shows the actionable error message.
    }
  }

  const handleBulkSubmit = async (values) => {
    const parsedValues = officeHoursValuesSchema.safeParse({
      ...values,
      afterHoursEnabled: true,
    })
    if (!parsedValues.success) {
      toast.error('Check the bulk office hours fields.', {
        description: parsedValues.error.issues[0]?.message,
      })
      return
    }

    const updates = selectedIds.map((buildingId) => ({
      buildingId,
      ...parsedValues.data,
    }))
    await saveUpdates(updates)
  }

  const handleSaveDrafts = async () => {
    const parsedUpdates = Object.entries(drafts).map(([buildingId, patch]) => {
      const row = rowsById.get(Number(buildingId))
      return row ? buildOfficeHoursUpdate(row, patch) : null
    })
    const invalidResult = parsedUpdates.find((result) => result && !result.success)
    if (invalidResult && !invalidResult.success) {
      toast.error('Check the edited office hours fields.', {
        description: invalidResult.error.issues[0]?.message,
      })
      return
    }
    const updates = parsedUpdates.flatMap((result) => (result?.success ? [result.data] : []))
    await saveUpdates(updates)
  }

  const pageError = accountsQuery.error || buildingsQuery.error

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="After-hours and office hours"
        description="Manage building schedules and the automated message sent outside office hours."
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => buildingsQuery.refetch()}
          disabled={!selectedAccountId || buildingsQuery.isFetching}
        >
          <ReloadIcon className="mr-2 h-4 w-4" />
          Refresh
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => exportRows(rows, drafts, selectedAccount?.accountName)}
          disabled={!rows.length}
        >
          <DownloadIcon className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </PageHeader>

      <div className="flex-1 space-y-6 overflow-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-2 sm:max-w-md">
              <Label htmlFor="office-hours-account">Account</Label>
              {accountsQuery.isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={selectedAccountId}
                  onValueChange={handleAccountChange}
                  disabled={!accounts.length || updateMutation.isPending}
                >
                  <SelectTrigger id="office-hours-account" data-pii="property">
                    <SelectValue placeholder="Select an active account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem
                        key={account.accountId}
                        value={String(account.accountId)}
                        data-pii="property"
                      >
                        {account.accountName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {pageError ? (
          <Alert variant="destructive">
            <AlertDescription>
              Office hours could not be loaded. Refresh the page. If the problem continues, contact
              engineering.
            </AlertDescription>
          </Alert>
        ) : null}

        {!selectedAccountId ? (
          <div className="rounded-md border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
            Select an account to load its buildings.
          </div>
        ) : (
          <>
            <BulkSetupForm
              selectedCount={selectedIds.length}
              onSubmit={handleBulkSubmit}
              isSaving={updateMutation.isPending}
            />
            <Card>
              <CardHeader>
                <CardTitle>Building settings</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Edit individual rows, then save the table edits. Building IDs remain hidden from
                  the interface but stay in CSV exports.
                </p>
              </CardHeader>
              <CardContent>
                <OfficeHoursTable
                  rows={rows}
                  drafts={drafts}
                  selectedIds={selectedIds}
                  search={search}
                  onSearchChange={setSearch}
                  onToggleBuilding={handleToggleBuilding}
                  onToggleVisible={handleToggleVisible}
                  onChangeRow={handleChangeRow}
                  onSave={handleSaveDrafts}
                  isLoading={buildingsQuery.isLoading}
                  isSaving={updateMutation.isPending}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <datalist id="office-hours-timezones">
        {TIME_ZONES.map((timeZone) => (
          <option key={timeZone} value={timeZone} />
        ))}
      </datalist>
    </div>
  )
}
